<section class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
  <div class="flex flex-wrap items-start justify-between gap-3">
    <div>
      <h2 class="text-lg font-semibold text-slate-900">{{ title }}</h2>
      <p class="mt-1 text-sm text-slate-500">{{ description }}</p>
    </div>
  </div>

  <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
    <div>
      <label for="date-preset" class="mb-1 block text-sm font-medium text-slate-700">Program Period</label>
      <select
        id="date-preset"
        class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
        [ngModel]="filters().datePreset"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="setDatePreset($event)"
      >
        @for (option of datePresetOptions; track option.value) {
          <option [ngValue]="option.value">{{ option.label }}</option>
        }
      </select>
    </div>
  </div>

  @if (filters().datePreset === 'custom') {
    <div class="mt-4 grid gap-4 md:grid-cols-2 xl:max-w-2xl">
      <div>
        <label for="custom-start" class="mb-1 block text-sm font-medium text-slate-700">Start Date</label>
        <input
          id="custom-start"
          type="date"
          class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
          [ngModel]="filters().startDate"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="setCustomDateStart($event)"
        />
      </div>

      <div>
        <label for="custom-end" class="mb-1 block text-sm font-medium text-slate-700">End Date</label>
        <input
          id="custom-end"
          type="date"
          class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
          [ngModel]="filters().endDate"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="setCustomDateEnd($event)"
        />
      </div>
    </div>
  }

  <button
    type="button"
    class="mt-5 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
    (click)="runReport()"
    [disabled]="loading()"
  >
    {{ loading() ? 'Running...' : 'Search' }}
  </button>

  @if (validationMessage()) {
    <div class="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      {{ validationMessage() }}
    </div>
  }

  @if (errorMessage()) {
    <div class="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {{ errorMessage() }}
    </div>
  }
</section>

<section class="mt-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
  @if (reportResult(); as result) {
    <div class="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 pb-4">
      <div>
        <h3 class="text-lg font-semibold text-slate-900">{{ result.title }}</h3>
        <p class="text-sm text-slate-500">{{ result.subtitle }}</p>
        <p class="mt-1 text-xs text-slate-500">{{ activeFilterSummary() }}</p>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        @if (canDownloadCsv()) {
          <a
            [href]="csvDownloadUrl()"
            [attr.download]="csvFileName()"
            class="rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100"
          >
            Download CSV
          </a>
        }
      </div>
    </div>

    @if (result.rows.length === 0) {
      <div class="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
        <div class="text-base font-semibold text-slate-900">No data found</div>
        <p class="mt-2 text-sm text-slate-600">Try changing the report filters and searching again.</p>
      </div>
    } @else {
      <div class="mt-5 hidden overflow-x-auto md:block">
        <table class="min-w-full border-collapse text-sm">
          <thead>
            <tr>
              @for (column of result.columns; track trackColumn($index, column)) {
                <th
                  class="border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700"
                  [class.text-right]="column.numeric"
                >
                  {{ column.label }}
                </th>
              }
            </tr>
          </thead>

          <tbody>
            @for (row of result.rows; track trackRow($index, row)) {
              <tr class="odd:bg-slate-50/60">
                @for (column of result.columns; track trackColumn($index, column)) {
                  <td class="border-b border-slate-100 px-3 py-2 text-slate-700" [class.text-right]="column.numeric">
                    {{ formatCellValue(row[column.key]) }}
                  </td>
                }
              </tr>
            }
          </tbody>
        </table>
      </div>

      <div class="mt-5 space-y-3 md:hidden">
        @for (row of result.rows; track trackRow($index, row)) {
          <article class="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <dl class="space-y-2">
              @for (column of result.columns; track trackColumn($index, column)) {
                <div class="flex items-start justify-between gap-4">
                  <dt class="text-xs font-semibold uppercase tracking-wide text-slate-500">{{ column.label }}</dt>
                  <dd class="text-right text-sm text-slate-800">{{ formatCellValue(row[column.key]) }}</dd>
                </div>
              }
            </dl>
          </article>
        }
      </div>
    }
  } @else {
    <div class="rounded-xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
      Run a report search to see results.
    </div>
  }
</section>
